import { useNavigate, } from 'react-router-dom';
import { Formik, Form, Field, FieldArray, } from 'formik';
import * as yup from 'yup';
import { v4 as uuidv4, } from 'uuid';
import { useSnackbar, } from 'notistack';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Stack,
    IconButton,
    Alert,
    Tooltip, } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useAuth, useFirestore, } from '../hooks';
import { escapeHTML, } from '../utils';

const initialValues = {
    title: '',
    description: '',
    terms: new Array(5).fill(null).map(() => ({ id: uuidv4(), eng: '', rus: '', })),
    minCards: '',
};

const validationSchema = yup.object({
    title: yup
        .string()
        .min(3, 'Название сета должно состоять минимум из 3 символов')
        .max(50, 'Название сета может состоять не более чем из 50 символов')
        .required('Название сета обязательно к заполнению'),
    description: yup
        .string()
        .max(500, 'Описание сета может состоять не более чем из 500 символов'),
});

const CreateSetPage = () => {
    const { user, } = useAuth();
    const { addDocument, response: { isPending, }, } = useFirestore('sets');
    const { enqueueSnackbar, } = useSnackbar();
    const navigate = useNavigate();

    const onSubmit = async ({ title, description, terms, }) => {
        const filledTerms = terms.filter((term) => term.eng && term.rus);

        const escapedTerms = filledTerms.map((term) => {
            return {
                ...term,
                eng: escapeHTML(term.eng),
                rus: escapeHTML(term.rus),
            };
        });

        const data = {
            title,
            uid: user.uid,
            modes: {
              learningCards: {
                  progress: 0,
                  answer: 'rus',
                  termsType: 'all',
                  random: false,
              },
              writeCards: {
                  progress: 0,
                  answer: 'rus',
                  termsType: 'all',
                  random: false,
              },
            },
            terms: escapedTerms,
        };

        if (description.trim()) {
            data.description = description;
        }

        const res = await addDocument(data);
        enqueueSnackbar(`Сет «${title}» создан`, { variant: 'success', });

        res && navigate(`/set/${res.id}`);
    };

    return (
        <>
            <Typography
                component='h2'
                sx={{
                    fontSize: [18, 21, 24, 27],
                    fontWeight: 700, mb: [3, 4, 5, 6], }}>Создание нового сета</Typography>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                validateOnChange={false}
                validateOnBlur={false}
            >
                {
                    (formik) => {
                        return (
                            <Form autoComplete='off'>
                                <Field name='title'>
                                    {
                                        (props) => {
                                            const { field, meta: { error, touched, }, } = props;

                                            return <TextField
                                                {...field}
                                                color='secondary'
                                                fullWidth
                                                label='Название сета'
                                                error={touched && Boolean(error)}
                                                helperText={touched && error}
                                                sx={{mb: [2, 2, 3],}}
                                            />
                                        }
                                    }
                                </Field>
                                <Field name='description'>
                                    {
                                        (props) => {
                                            const { field, meta: { error, touched, }, } = props;

                                            return <TextField
                                                {...field}
                                                color='secondary'
                                                fullWidth
                                                label='Краткое описание (не обязательно)'
                                                error={touched && Boolean(error)}
                                                helperText={touched && error}
                                                sx={{mb: 5,}}
                                            />
                                        }
                                    }
                                </Field>

                                <FieldArray name='terms'>
                                    {
                                        (fieldArrayProps) => {
                                            const { push, remove, form, } = fieldArrayProps;
                                            const { values: { terms, }, } = form;

                                            return (
                                                <>
                                                    {
                                                        terms.map((term, index) => {
                                                            return (
                                                                <Box key={term.id}>
                                                                    <Grid
                                                                        container
                                                                        sx={{
                                                                            bgcolor: (theme) => theme.palette.mode === 'light' ? (index % 2) ? 'grey.50' : 'grey.200' : (index % 2) ? 'common.rhino' : 'common.shark',
                                                                            border: 1,
                                                                            borderColor: (theme) => theme.palette.mode === 'light' ? 'borderColor.light' : 'common.pattensBlue',
                                                                            mb: ((terms.length - 1) === index) ? 0 : 2.5,
                                                                            py: 3,
                                                                            px: 3.5, }}>
                                                                        <Grid item xs={12}>
                                                                            <Stack direction='row' justifyContent='space-between' sx={{mb: 3,}}>
                                                                                <Box
                                                                                    component='h3'
                                                                                    sx={{
                                                                                        color: 'text.primary',
                                                                                        fontSize: 16,
                                                                                        fontWeight: 500,
                                                                                        lineHeight: 1.5,
                                                                                        m: 0, }}>Карточка #{index + 1}</Box>
                                                                                <Tooltip title={(terms.length > 2) ? 'Удалить карточку' : 'Невозможно удалить карточку'} placement='top' arrow>
                                                                                    <Box component='span'>
                                                                                        <IconButton
                                                                                            disabled={terms.length <= 2}
                                                                                            onClick={() => remove(index)}
                                                                                            sx={{
                                                                                                p: 0,
                                                                                                '&:disabled': {
                                                                                                    opacity: 0.7,
                                                                                                },
                                                                                                '&:hover': {
                                                                                                    bgcolor: 'transparent',
                                                                                                    '.MuiSvgIcon-root': {
                                                                                                        color: 'secondary.main',
                                                                                                    },
                                                                                                }
                                                                                            }}>
                                                                                            <DeleteIcon sx={{
                                                                                                color: 'text.primary',
                                                                                                fontSize: 20,
                                                                                            }} />
                                                                                        </IconButton>
                                                                                    </Box>
                                                                                </Tooltip>
                                                                            </Stack>
                                                                        </Grid>
                                                                        <Grid item xs={12} md={6} sx={{pr: {md: 1}, }}>
                                                                            <Field name={`terms[${index}].eng`} validate={(value) => {
                                                                                let error;

                                                                                if (formik.values.terms[index].rus && !(value.trim())) {
                                                                                    error = 'Вы забыли заполнить слово';
                                                                                } else if (value.length > 150) {
                                                                                    error = 'Допустимое количество символов 150'
                                                                                }

                                                                                return error;
                                                                            }}>
                                                                                {
                                                                                    (props) => {
                                                                                        const { field, meta: { touched, error, }, } = props;

                                                                                        return (
                                                                                            <TextField
                                                                                                {...field}
                                                                                                color='secondary'
                                                                                                fullWidth
                                                                                                placeholder='Английское слово'
                                                                                                helperText={touched && error}
                                                                                                sx={{
                                                                                                    mb: 1.5,
                                                                                                    '& .MuiInputBase-input': {
                                                                                                        border: 1,
                                                                                                        borderColor: 'common.pattensBlue',
                                                                                                        color: 'text.primary',
                                                                                                        p: 1.5,
                                                                                                    },
                                                                                                    '.MuiFormHelperText-root': {
                                                                                                        color: 'red',
                                                                                                    },
                                                                                                }} />
                                                                                        )
                                                                                    }
                                                                                }
                                                                            </Field>
                                                                        </Grid>
                                                                        <Grid item xs={12} md={6} sx={{pl: {md: 1},}}>
                                                                            <Field name={`terms[${index}].rus`} validate={(value) => {
                                                                                let error;

                                                                                if (formik.values.terms[index].eng && !(value.trim())) {
                                                                                    error = 'Вы забыли заполнить перевод';
                                                                                } else if (value.length > 200) {
                                                                                    error = 'Допустимое количество символов 200'
                                                                                }

                                                                                return error;
                                                                            }}>
                                                                                {
                                                                                    (props) => {
                                                                                        const { field, meta: { touched, error, }, } = props;

                                                                                        return (
                                                                                            <TextField
                                                                                                {...field}
                                                                                                color='secondary'
                                                                                                fullWidth
                                                                                                placeholder='Перевод на русский'
                                                                                                helperText={touched && error}
                                                                                                sx={{
                                                                                                    '& .MuiInputBase-input': {
                                                                                                        border: 1,
                                                                                                        borderColor: 'common.pattensBlue',
                                                                                                        color: 'text.primary',
                                                                                                        p: 1.5,
                                                                                                    },
                                                                                                    '.MuiFormHelperText-root': {
                                                                                                        color: 'red',
                                                                                                    },
                                                                                                }} />
                                                                                        )
                                                                                    }
                                                                                }
                                                                            </Field>
                                                                        </Grid>
                                                                    </Grid>
                                                                    {
                                                                        ((terms.length - 1) === index) &&
                                                                        <>
                                                                            <Field name='minCards' validate={() => {
                                                                                let error;

                                                                                const colFilled = formik.values.terms.filter((term) => term.eng && term.rus).length;
                                                                                if (colFilled < 2) {
                                                                                    error = 'Необходимо заполнить минимум две карточки';
                                                                                }

                                                                                return error;
                                                                            }}>
                                                                                {
                                                                                    (props) => {
                                                                                        const { meta: { error, }, } = props;

                                                                                        if (error) {
                                                                                            return (<Alert
                                                                                                severity='error'
                                                                                                variant='filled'
                                                                                                sx={{mt: 2.5,}}>
                                                                                                {error}
                                                                                            </Alert>)
                                                                                        }

                                                                                        return null;
                                                                                    }
                                                                                }
                                                                            </Field>
                                                                            <Grid spacing={1.5} container sx={{mt: [5, 6, 7],}}>
                                                                                <Grid xs={12} md={6} item>
                                                                                    <Button
                                                                                        fullWidth
                                                                                        startIcon={<AddIcon />}
                                                                                        onClick={() => { push({ id: uuidv4(), eng: '', rus: '', }) }}
                                                                                        sx={{
                                                                                            bgcolor: 'common.green',
                                                                                            boxShadow: 2,
                                                                                            color: 'common.white',
                                                                                            py: 1.5,
                                                                                            px: 2,
                                                                                            '&:hover': {
                                                                                                bgcolor: 'common.green',
                                                                                            },
                                                                                        }}>Добавить карточку</Button>
                                                                                </Grid>
                                                                                <Grid xs={12} md={6} item>
                                                                                    <Button
                                                                                        fullWidth
                                                                                        type='submit'
                                                                                        disabled={isPending || !formik.dirty}
                                                                                        onClick={() => formik.validateForm()}
                                                                                        sx={{
                                                                                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'common.rhino' : 'common.kashmirBlue',
                                                                                            boxShadow: 2,
                                                                                            color: 'common.white',
                                                                                            py: 1.5,
                                                                                            px: 2,
                                                                                            '&:hover': {
                                                                                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'common.rhino' : 'common.kashmirBlue',
                                                                                            },
                                                                                            '&:disabled': {
                                                                                                color: 'rgba(255, 255, 255, 0.26)',
                                                                                            },
                                                                                        }}>{ isPending ? 'Создаём...' : 'Готово' }</Button>
                                                                                </Grid>
                                                                            </Grid>
                                                                        </>
                                                                    }
                                                                </Box>
                                                            )
                                                        })
                                                    }
                                                </>
                                                );
                                        }
                                    }
                                </FieldArray>
                            </Form>
                        )
                    }
                }
            </Formik>
        </>
    )
};

export default CreateSetPage;